import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CheckCircle2, XCircle, Copy, PlugZap, ShieldCheck, KeyRound } from "lucide-react";
import { api, BACKEND_URL, currentRole } from "@/lib/api";
import { fmtDate, errMsg } from "./shared";
import { LiveChecksCard, MfaConfirm } from "./LiveChecksCard";

export default function PaymentGateway() {
  const [s, setS] = useState(null);
  const [mfaAction, setMfaAction] = useState(null);
  const isSA = currentRole() === "super_admin";
  const load = () => api.get("/admin/payment-gateway").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!s) return <div className="text-slate-400">Loading…</div>;

  const webhookUrl = `${BACKEND_URL}${s.webhook_path}`;

  return (
    <div data-testid="payment-gateway-page" className="max-w-5xl">
      <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold">Super Admin · Payments</div>
      <h1 className="font-display text-3xl font-bold mt-1">Payment Gateway</h1>
      <p className="text-sm text-slate-400 mt-1 mb-6">Razorpay keys live only in secure server settings. The live site accepts live keys only. Customers can pay online only after every live check passes.</p>

      <StatusBanner s={s}/>
      {s.mode_error && <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300" data-testid="pg-mode-error">{s.mode_error}</div>}

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-5 mt-5">
        <ServerKeysCard s={s}/>
        <div className="space-y-5">
          <div className="admin-card p-5" data-testid="pg-mode-card">
            <div className="font-bold mb-1">Mode</div>
            <p className="text-xs text-slate-500 mb-3">Set by the Key ID. Only live keys are accepted.</p>
            <span className={`inline-block px-5 py-2 rounded-lg text-sm font-semibold uppercase tracking-wider ${s.configured ? (s.environment === "live" ? "bg-emerald-600 text-white" : "bg-amber-500 text-slate-950") : "bg-slate-800 text-slate-400"}`} data-testid="pg-env-label">{s.configured ? s.environment : "not set"}</span>
          </div>
          <div className="admin-card p-5" data-testid="pg-connection-card">
            <div className="font-bold mb-3 flex items-center gap-2"><PlugZap size={16} className="text-orange-400"/>Connection</div>
            <div className="text-xs text-slate-400 space-y-1">
              <div>Status: <b className={s.connection_status === "ok" ? "text-emerald-400" : "text-slate-300"} data-testid="pg-connection-status">{s.connection_status || "not tested"}</b></div>
              <div>Last tested: {fmtDate(s.last_tested_at)}</div>
              {s.last_error && <div className="text-red-300">{s.last_error}</div>}
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600" disabled={!s.key_secret_set} onClick={async () => { try { const { data } = await api.post("/admin/integrations/razorpay/test"); data.ok ? toast.success(data.detail) : toast.error(data.detail); } catch (e) { toast.error(errMsg(e)); } load(); }} data-testid="pg-test-btn">Test connection</Button>
            </div>
          </div>
          <WebhookCard url={webhookUrl} events={s.webhook_events}/>
        </div>
      </div>
      {isSA && <LiveChecksCard s={s} onChange={load} askMfa={setMfaAction}/>}
      <OnlinePaymentsCard s={s} isSA={isSA} onToggle={(enabled) => setMfaAction({
        title: enabled ? "Turn ON online payments" : "Turn OFF online payments",
        run: async (code) => { const { data } = await api.post("/admin/payment-gateway/online-payments", { enabled, mfa_code: code }); toast.success("Saved"); setS(data); },
      })}/>
      {isSA && <WebhookEvents/>}
      {mfaAction && <MfaConfirm action={mfaAction} onClose={() => setMfaAction(null)}/>}
    </div>
  );
}

function StatusBanner({ s }) {
  const live = !s.requests_only;
  return (
    <div className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${live ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10"}`} data-testid="pg-status-banner">
      <div>
        <div className={`font-bold ${live ? "text-emerald-300" : "text-amber-300"}`}>{live ? "Online payments are LIVE" : "Booking-request mode — no online payments"}</div>
        <div className="text-xs text-slate-400 mt-0.5">{live ? `Customers pay via Razorpay (${s.environment} mode).` : "Customers submit requests; your team arranges payment offline."}</div>
      </div>
      <span className="font-mono text-xs uppercase tracking-widest text-slate-400">gateway: <b className="text-slate-200" data-testid="pg-mode-label">{s.mode === "mock" ? "not connected" : `${s.mode} connected`}</b></span>
    </div>
  );
}

function ServerKeysCard({ s }) {
  const set = { RAZORPAY_KEY_ID: !!s.key_id_masked, RAZORPAY_KEY_SECRET: s.key_secret_set, RAZORPAY_WEBHOOK_SECRET: s.webhook_secret_set };
  return (
    <div className="admin-card p-5 self-start" data-testid="pg-credentials-card">
      <div className="font-bold mb-1 flex items-center gap-2"><KeyRound size={16} className="text-orange-400"/>Razorpay keys (server settings)</div>
      <p className="text-xs text-slate-500 mb-4">Kept only on the server and never sent to the browser. Changes apply after the next publish.</p>
      <div className="space-y-2">
        {s.env_keys.map((k) => (
          <div key={k} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 px-3 py-2" data-testid={`pg-key-${k}`}>
            <code className="text-xs text-slate-300 break-all">{k}</code>
            <span className={`text-xs font-semibold shrink-0 ${set[k] ? "text-emerald-400" : "text-slate-500"}`}>{set[k] ? (k === "RAZORPAY_KEY_ID" ? s.key_id_masked : "set") : "not set"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WebhookEvents() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/razorpay-webhook-events?limit=20").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const tone = { processed: "text-emerald-400", error: "text-red-400", amount_mismatch: "text-red-400", ignored: "text-slate-500" };
  return (
    <div className="admin-card p-5 mt-5" data-testid="pg-webhook-events">
      <div className="flex items-center justify-between mb-3">
        <div className="font-bold">Recent webhook events</div>
        <Button size="sm" variant="ghost" className="text-slate-400" onClick={load} data-testid="pg-webhook-events-refresh">Refresh</Button>
      </div>
      {!items ? <div className="text-xs text-slate-500">Loading…</div> : !items.length ? <div className="text-xs text-slate-500" data-testid="pg-webhook-events-empty">No webhook events received yet.</div> : (
        <div className="divide-y divide-slate-800 text-xs">
          {items.map((e) => (
            <div key={e.event_id} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`pg-webhook-event-${e.event_id}`}>
              <span className="font-mono text-slate-300 w-36">{e.event}</span>
              <span className="text-slate-500 truncate flex-1 min-w-0">{e.summary?.payment_id || e.summary?.order_id || e.summary?.refund_id || "—"}</span>
              <span className={`font-semibold ${tone[e.status] || "text-amber-300"}`}>{e.status}{e.deliveries > 1 ? ` ×${e.deliveries}` : ""}</span>
              <span className="text-slate-500">{fmtDate(e.received_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WebhookCard({ url, events }) {
  return (
    <div className="admin-card p-5" data-testid="pg-webhook-card">
      <div className="font-bold mb-2">Webhook URL</div>
      <div className="flex gap-2">
        <code className="flex-1 text-[11px] bg-slate-950 border border-slate-800 rounded px-2 py-2 break-all text-slate-300" data-testid="pg-webhook-url">{url}</code>
        <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Copied"))} data-testid="pg-copy-webhook"><Copy size={14}/></Button>
      </div>
      <div className="text-[11px] text-slate-500 mt-2">Subscribe to: {events.join(", ")}</div>
    </div>
  );
}

function OnlinePaymentsCard({ s, isSA, onToggle }) {
  return (
    <div className="admin-card p-5 mt-5" data-testid="pg-online-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-bold flex items-center gap-2"><ShieldCheck size={16} className="text-orange-400"/>Accept online payments</div>
          <p className="text-xs text-slate-500 mt-1">All checks below must pass before customers can pay online.</p>
        </div>
        <Switch checked={s.online_payments_enabled} disabled={!isSA || (!s.online_payments_enabled && s.checklist.some((c) => !c.ok))} onCheckedChange={onToggle} data-testid="pg-online-switch"/>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
        {s.checklist.map((c) => (
          <div key={c.key} className="flex items-center gap-2 text-xs rounded-lg border border-slate-800 px-3 py-2" data-testid={`pg-check-${c.key}`}>
            {c.ok ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0"/> : <XCircle size={14} className="text-slate-600 shrink-0"/>}
            <span className={c.ok ? "text-slate-200" : "text-slate-500"}>{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
